import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import PageHeader from '../../components/PageHeader.jsx';
import StatusBadge from '../../components/StatusBadge.jsx';
import Loader, { ErrorBox } from '../../components/Loader.jsx';
import { TasksAPI } from '../../lib/api';
import { buildPath, TASK_STATUS } from '../../config';

export default function TaskDetails() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [task, setTask] = useState(null);
  const [comments, setComments] = useState([]);
  const [comment, setComment] = useState('');
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const reload = () => {
    setLoading(true);
    Promise.all([TasksAPI.retrieve(id), TasksAPI.comments(id)])
      .then(([t, c]) => {
        setTask(t);
        setComments(c.results || c);
      })
      .catch(setError)
      .finally(() => setLoading(false));
  };

  useEffect(reload, [id]);

  const onStatusChange = async (newStatus) => {
    setBusy(true);
    try {
      const updated = await TasksAPI.setStatus(id, newStatus);
      setTask(updated);
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  };

  const onAddComment = async (e) => {
    e.preventDefault();
    if (!comment.trim()) return;
    setBusy(true);
    try {
      await TasksAPI.addComment(id, comment.trim());
      setComment('');
      const c = await TasksAPI.comments(id);
      setComments(c.results || c);
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  const onDelete = async () => {
    if (!window.confirm('Delete this task? This cannot be undone.')) return;
    setBusy(true);
    try {
      await TasksAPI.remove(id);
      navigate(buildPath.tasksList('my_tasks'));
    } catch (e) {
      setError(e);
      setBusy(false);
    }
  };

  if (loading) return <Loader />;
  if (!task) return <ErrorBox error={error || new Error('Task not found.')} />;

  return (
    <>
      <PageHeader
        title={task.title}
        subtitle={
          <>
            Created {task.created_on && new Date(task.created_on).toLocaleDateString()}
            {task.created_by_email ? ` by ${task.created_by_email}` : ''}
          </>
        }
        actions={
          <>
            <Link className="ty-btn ty-btn--ghost" to={buildPath.taskEdit(task.id)}>Edit</Link>
            <button className="ty-btn ty-btn--danger" onClick={onDelete} disabled={busy}>Delete</button>
          </>
        }
      />

      <ErrorBox error={error} />

      <div className="ty-card">
        <div className="ty-row" style={{ justifyContent: 'space-between' }}>
          <StatusBadge domain="task" value={task.status} />
          <select
            className="ty-select"
            value={task.status}
            onChange={(e) => onStatusChange(e.target.value)}
            disabled={busy}
            style={{ width: 'auto' }}
          >
            {TASK_STATUS.map((s) => (
              <option key={s.value} value={s.value}>Set to: {s.label}</option>
            ))}
          </select>
        </div>

        <hr />

        <p style={{ whiteSpace: 'pre-wrap' }}>{task.description || <em className="ty-muted">No description.</em>}</p>

        <div className="ty-grid-2 ty-mt-2">
          <div><strong>Start:</strong> {task.start_date || '—'}</div>
          <div><strong>End:</strong> {task.end_date || '—'}</div>
          <div><strong>Urgency:</strong> {task.urgency}</div>
          <div><strong>Estimated:</strong> {task.estimated_hours ?? '—'} hrs</div>
          <div><strong>Team:</strong> {task.team}</div>
          <div><strong>Project:</strong> {task.project_name || '—'}</div>
        </div>
      </div>

      <div className="ty-card ty-mt-3">
        <h3>Comments</h3>
        {comments.length === 0 && <div className="ty-empty">No comments yet.</div>}
        {comments.map((c) => (
          <div key={c.id} style={{ borderBottom: '1px solid var(--ty-border)', padding: '8px 0' }}>
            <div className="ty-muted" style={{ fontSize: 12 }}>
              {c.created_by_email} · {c.date && new Date(c.date).toLocaleString()}
            </div>
            <div style={{ whiteSpace: 'pre-wrap' }}>{c.comment}</div>
          </div>
        ))}

        <form onSubmit={onAddComment} className="ty-mt-2">
          <textarea
            className="ty-textarea"
            placeholder="Add a comment…"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
          />
          <div className="ty-row ty-row--end ty-mt-2">
            <button className="ty-btn" type="submit" disabled={busy || !comment.trim()}>
              Post
            </button>
          </div>
        </form>
      </div>
    </>
  );
}
